import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Plus as PlusIcon } from 'react-feather';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import NortEastIcon from '@mui/icons-material/NorthEast';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SortIcon from '@mui/icons-material/Sort';
import { ChatContextType } from '@trinity/chat';
import {
  AuraChatContext,
  BrandInsightsModalContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat';
import {
  ActiveChatSessionContext,
  ActivePageContext,
  ChatPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterButton } from '@trinity/chat-components/components/Chat.Filters';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container';
import {
  ChatHistory,
  ChatHistoryItemsContext,
  ChatHistorySharedContext,
} from '@trinity/chat-components/components/Chat.History/index.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { Constants } from '@trinity/chat-components/components/Theme';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import { RateLimitingContext } from '@trinity/chat-components/containers/RateLimiting/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';

type Props = unknown;

export const ChatSidePanel = (props: Props) => {
  const pageContext = useContext(ChatPageContextDataContext);
  const chat = useContext(ActiveChatSessionContext);
  const { pageId: activePageId, layout: activeLayout } = useContext(ActivePageContext);
  const [isExpanded, setIsExpanded] = useContext(SidePanelExpandedContext);
  const [, toggleModal] = useContext(BrandInsightsModalContext);
  const [showHistory, setShowHistory] = useState(false);
  const items = useContext(ChatHistoryItemsContext);
  const shared = useContext(ChatHistorySharedContext);

  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const chatWelcomeScreen = useContext(ChatWelcomeScreenContext);
  const { query } = useContext(RateLimitingContext);
  const { setAuraChatStatus } = useContext(AuraChatContext);
  const [width, setWidth] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const _toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  const handleNewChat = useCallback(() => {
    setAuraChatStatus(false);
    chatWelcomeScreen?.dismiss();

    const context = chat?.context
      ? {
          ...chat.context,
          immutableFilters: pageContext?.immutableFilters,
        }
      : pageContext;

    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    if (!brandIdentifier) {
      console.error('Brand identifier is required to start a new chat');
      return;
    }

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [setAuraChatStatus, chatWelcomeScreen, dispatch, chat, pageContext, brandIdentifier]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = wrapRef.current?.offsetWidth || 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const diff = startX - moveEvent.clientX;
      const newWidth = Math.max(300, startWidth + diff);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const isRateLimitExceeded = useMemo(() => {
    return query?.data?.allowed === false;
  }, [query?.data?.allowed]);

  if (!isExpanded) return null;
  if (activePageId != null && activeLayout === 'full_page') return null;

  return (
    <Root>
      <ResizeHandle onMouseDown={handleResizeStart} />
      <Wrap showHistory={showHistory} isExpanded={isExpanded} customWidth={width} ref={wrapRef}>
        <Content>
          <Collapsed isExpanded={isExpanded}></Collapsed>
          <Expanded isExpanded={isExpanded}>
            <Header>
              <Menubar>
                <Title>Discover Insights</Title>

                <PanelIcons>
                  <PanelIcon>
                    <PanelIconChatBottomIcon />
                  </PanelIcon>
                  <PanelIcon onClick={toggleModal}>
                    <PanelIconChatModalIcon />
                  </PanelIcon>
                  <PanelIcon>
                    <PanelIconCloseIcon />
                  </PanelIcon>
                </PanelIcons>
              </Menubar>

              <ActionsBar>
                <ActionsLeft>
                  <HistoryIcon onClick={() => setShowHistory(!showHistory)} />
                  <ContextSelectorContainer>
                    <FilterButton
                      css={{ background: 'transparent', padding: 0, marginTop: 4 }}
                      placement="bottom-end"
                    />
                  </ContextSelectorContainer>
                </ActionsLeft>

                <Action>
                  <NewChatButton disabled={isRateLimitExceeded} onClick={handleNewChat}>
                    <StyledPlusIcon />
                  </NewChatButton>
                </Action>
              </ActionsBar>
            </Header>
            {showHistory && <ChatHistory items={items} shared={shared} />}
            {!showHistory && <ChatInstance isSidePanel={true} />}
          </Expanded>
        </Content>
      </Wrap>
    </Root>
  );
};

ChatSidePanel.displayName = 'Chat.SidePanel';

const Root = styled.div`
  box-sizing: border-box;

  border-top-left-radius: ${Constants.Modal.BorderRadius}px;
  border-bottom-left-radius: ${Constants.Modal.BorderRadius}px;

  height: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
  position: relative;
`;

const Wrap = styled.div<BarProps>`
  position: relative;
  width: ${(props) => (props.customWidth !== null ? `${props.customWidth}px` : '450px')};
  transition: ${(props) => (props.customWidth !== null ? 'none' : 'width 500ms')};
  height: 100%;
  background: ${(props) => (props.showHistory ? '#f2f2f2' : 'var(--pri-01)')};
`;

const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  z-index: 100;

  &:hover {
    background: #0078d4;
  }
`;

const Header = styled.div`
  width: 100%;
`;

const Menubar = styled.div`
  background: #f2f2f2;
  height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;

const PanelIcons = styled.div`
  display: flex;
  gap: 5px;
`;

const PanelIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: ${(props) => props.theme.palette.btnPrimary.bg};
  color: ${(props) => props.theme.palette.btnPrimary.color};
  border-radius: 6px;
  width: 24px;
  height: 24px;
  transition:
    background-color 200ms ease-in-out,
    border 140ms ease-in,
    border-color 140ms ease-in,
    color 140ms ease-in;

  &:hover {
    background-color: ${(props) => props.theme.palette.btnPrimary.active.bg};
    color: ${(props) => props.theme.palette.btnPrimary.active.color};
  }
`;

const PanelIconChatBottomIcon = styled(NortEastIcon)`
  width: 18px;
  height: 18px;
`;

const PanelIconChatModalIcon = styled(OpenInFullIcon)`
  width: 18px;
  height: 18px;
`;

const PanelIconCloseIcon = styled(CloseIcon)`
  width: 18px;
  height: 18px;
`;

const ActionsBar = styled.div`
  height: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;

const Title = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const Content = styled.div`
  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow: hidden;

  height: 100%;
`;

const Expanded = styled.div<BarProps>`
  display: ${(props) => (props.isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;

  opacity: ${(props) => (props.isExpanded ? 1 : 0)};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const Collapsed = styled.div<BarProps>`
  display: ${(props) => (props.isExpanded ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;

  opacity: ${(props) => (props.isExpanded ? 0 : 1)};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const StyledPlusIcon = styled(PlusIcon)`
  color: ${(props) => props.theme.palette.secondary.main};
  stroke-width: 3;
  width: 14px;
  min-width: 14px;
`;

const NewChatButton = styled.button`
  border-radius: 50px;
  background-color: ${({ theme }) => theme.palette.sentiment.primary.main};
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  &:disabled {
    background-color: ${(props) => props.theme.palette.gray.light1};
    cursor: not-allowed;
  }
`;

const Action = styled.div``;

const ActionsLeft = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const HistoryIcon = styled(SortIcon)`
  height: 28px;
  width: 28x;
  cursor: pointer;
  color: var(--gray-d);
  transition: color 140ms ease-in;

  &:hover {
    color: ${(props) => props.theme.palette.sentiment.primary.main};
  }
`;

type BarProps = {
  isExpanded?: boolean;
  customWidth?: number | null;
  showHistory?: boolean;
};
